// AdminCalendarView.js - New component for admin calendar view
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Grid
} from "@mui/material";
import {
    CalendarToday as CalendarIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon
} from "@mui/icons-material";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
    collection,
    query,
    where,
    getDocs,
    orderBy
} from "firebase/firestore";
import { db } from "../firebase";

const AdminCalendarView = ({ orgId }) => {
    const [timeOffEvents, setTimeOffEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [monthlyStats, setMonthlyStats] = useState({
        totalAbsences: 0,
        affectedEmployees: 0,
        mostCommonType: ''
    });

    useEffect(() => {
        if (orgId) {
            fetchEmployees();
            fetchTimeOffData();
        }
    }, [orgId]);

    const fetchEmployees = async () => {
        try {
            const employeesRef = collection(db, "organizations", orgId, "employees");
            const employeesSnapshot = await getDocs(employeesRef);

            const employeeMap = new Map();
            employeesSnapshot.docs.forEach(doc => {
                const data = doc.data();
                employeeMap.set(doc.id, data);
                if (data.email) {
                    employeeMap.set(data.email.toLowerCase(), data);
                }
            });

            setEmployees(Array.from(employeeMap.values()));
        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const fetchTimeOffData = async () => {
        setLoading(true);
        try {
            // Fetch approved time-off requests
            const timeOffRef = collection(db, "organizations", orgId, "timeOffRequests");
            const q = query(
                timeOffRef,
                where("status", "==", "approved"),
                orderBy("startDate", "desc")
            );
            const snapshot = await getDocs(q);

            const employeeMap = new Map();
            employees.forEach(emp => {
                employeeMap.set(emp.id, emp);
                if (emp.email) {
                    employeeMap.set(emp.email.toLowerCase(), emp);
                }
            });

            const events = snapshot.docs.map(doc => {
                const data = doc.data();

                // Handle date conversion
                let startDate, endDate;
                if (data.startDate && typeof data.startDate.toDate === 'function') {
                    startDate = data.startDate.toDate();
                } else {
                    startDate = new Date(data.startDate);
                }

                if (data.endDate && typeof data.endDate.toDate === 'function') {
                    endDate = data.endDate.toDate();
                } else {
                    endDate = new Date(data.endDate);
                }

                // Adjust end date for FullCalendar (exclusive end dates)
                const adjustedEndDate = new Date(endDate);
                adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);

                // Reset time to midnight
                startDate.setHours(0, 0, 0, 0);
                adjustedEndDate.setHours(0, 0, 0, 0);

                // Determine employee name
                let employeeName = "Unknown";
                if (data.employeeName) {
                    employeeName = data.employeeName;
                } else if (data.userId && employeeMap.has(data.userId)) {
                    const employee = employeeMap.get(data.userId);
                    employeeName = employee.name || employee.displayName || employee.email;
                } else if (data.employeeEmail && employeeMap.has(data.employeeEmail.toLowerCase())) {
                    const employee = employeeMap.get(data.employeeEmail.toLowerCase());
                    employeeName = employee.name || employee.displayName || employee.email;
                } else if (data.employeeEmail) {
                    const emailParts = data.employeeEmail.split('@');
                    if (emailParts.length > 0) {
                        const namePart = emailParts[0]
                            .replace(/\./g, ' ')
                            .replace(/(\b\w)/g, match => match.toUpperCase());
                        employeeName = namePart;
                    }
                }

                const duration = Math.ceil((adjustedEndDate - startDate) / (1000 * 60 * 60 * 24));

                return {
                    id: doc.id,
                    title: `${employeeName} - ${data.type || 'Time Off'}`,
                    start: startDate,
                    end: adjustedEndDate,
                    allDay: true,
                    backgroundColor: getTimeOffTypeColor(data.type),
                    borderColor: getTimeOffTypeColor(data.type),
                    extendedProps: {
                        employeeId: data.userId,
                        employeeName: employeeName,
                        email: data.employeeEmail || data.email,
                        requestType: data.type,
                        reason: data.reason,
                        duration: duration,
                        requestDate: data.requestDate,
                        approvedDate: data.responseDate
                    }
                };
            });

            setTimeOffEvents(events);
            calculateMonthlyStats(events);
        } catch (err) {
            console.error("Error fetching time off data:", err);
            setError("Failed to load calendar data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const calculateMonthlyStats = (events) => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        const currentMonthEvents = events.filter(event => {
            const eventStart = new Date(event.start);
            return eventStart.getMonth() === currentMonth && eventStart.getFullYear() === currentYear;
        });

        const uniqueEmployees = new Set(currentMonthEvents.map(e => e.extendedProps.employeeId));
        const typeCount = {};
        currentMonthEvents.forEach(event => {
            const type = event.extendedProps.requestType || 'other';
            typeCount[type] = (typeCount[type] || 0) + 1;
        });

        const mostCommonType = Object.keys(typeCount).reduce((a, b) =>
            typeCount[a] > typeCount[b] ? a : b, 'none'
        );

        setMonthlyStats({
            totalAbsences: currentMonthEvents.length,
            affectedEmployees: uniqueEmployees.size,
            mostCommonType: typeCount[mostCommonType] > 0 ? mostCommonType : 'none'
        });
    };

    const getTimeOffTypeColor = (type) => {
        const colors = {
            'vacation': '#4CAF50',
            'sick': '#F44336',
            'personal': '#2196F3',
            'bereavement': '#9C27B0',
            'jury duty': '#FF9800',
            'maternity': '#E91E63',
            'paternity': '#E91E63',
            'military': '#795548',
            'training': '#009688',
            'unpaid': '#607D8B'
        };
        return colors[type?.toLowerCase()] || '#607D8B';
    };

    const handleEventClick = (clickInfo) => {
        const event = clickInfo.event;
        setSelectedEvent({
            title: event.title,
            start: event.start,
            end: new Date(event.end.getTime() - 24 * 60 * 60 * 1000), // Adjust for display
            ...event.extendedProps
        });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedEvent(null);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
    }

    return (
        <Box>
            {/* Monthly Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CalendarIcon color="primary" sx={{ mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" component="div">
                                        {monthlyStats.totalAbsences}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        Total Absences This Month
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PeopleIcon color="primary" sx={{ mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" component="div">
                                        {monthlyStats.affectedEmployees}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        Employees Taking Time Off
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TrendingUpIcon color="primary" sx={{ mr: 2 }} />
                                <Box>
                                    <Typography variant="h6" component="div" sx={{ textTransform: 'capitalize' }}>
                                        {monthlyStats.mostCommonType}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        Most Common Type
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Calendar */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Team Absence Calendar
                    </Typography>

                    <Box sx={{ height: 'calc(100vh - 400px)', minHeight: '600px' }}>
                        <FullCalendar
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{
                                left: 'prev,next today',
                                center: 'title',
                                right: 'dayGridMonth,dayGridWeek'
                            }}
                            events={timeOffEvents}
                            eventClick={handleEventClick}
                            height="100%"
                            eventDisplay="block"
                            dayMaxEvents={3}
                            moreLinkClick="popover"
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Event Details Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Time Off Details
                </DialogTitle>
                <DialogContent>
                    {selectedEvent && (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="h6" gutterBottom>
                                {selectedEvent.employeeName}
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Email
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedEvent.email || 'N/A'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Type
                                    </Typography>
                                    <Chip
                                        label={selectedEvent.requestType || 'Time Off'}
                                        color="primary"
                                        size="small"
                                        sx={{ mb: 2 }}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Start Date
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {formatDate(selectedEvent.start)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        End Date
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {formatDate(selectedEvent.end)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Duration
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedEvent.duration} day{selectedEvent.duration !== 1 ? 's' : ''}
                                    </Typography>
                                </Grid>

                                {selectedEvent.reason && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            Reason
                                        </Typography>
                                        <Typography variant="body1" gutterBottom>
                                            {selectedEvent.reason}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminCalendarView;

